'use client';

// client/src/app/workspace-chat-context.tsx
import { useSession } from "next-auth/react";
import React, { createContext, useContext, useEffect, useState } from 'react';

export type SuggestedQuestion = {
  id: string;
  text: string;
};

export type Workspace = {
  id: string;
  name: string;
  createdAt: number;
  fileUrls?: string[];
  locked?: boolean;
  suggestedQuestions?: SuggestedQuestion[];
  isSpecialAgent?: boolean;
};

interface WorkspaceChatContextType {
  workspaces: Workspace[];
  setWorkspaces: (workspaces: Workspace[]) => void;
  loading: boolean;
  addWorkspace: (workspace: Workspace) => void;
  workspace: Workspace | null;
  setWorkspace: (workspace: Workspace | null) => void;
}

const WorkspaceChatContext = createContext<WorkspaceChatContextType>({
  workspaces: [],
  setWorkspaces: () => {},
  loading: true,
  addWorkspace: () => {},
  workspace: null,
  setWorkspace: () => {},
});

const WORKSPACE_QUESTIONS: Record<string, SuggestedQuestion[]> = {
  'Sales': [
    { id: '1', text: 'Which sales reps cover the Hartford location?' },
    { id: '2', text: 'How do I handle an out of round adjustment for Milestar?' },
    { id: '3', text: 'What is the transfer schedule for Compton?' }
  ],
  'Tireco-Product-Book': [
    { id: '1', text: 'What is the mileage warranty on the as710?' },
    { id: '2', text: 'How does the Patagonia AT Pro compare to the Toyo AT 3?' },
    { id: '3', text: 'What sizes are available in the as810?' }
  ],
  'Writing-Assistant': [
    { id: '1', text: 'Can you help me improve this paragraph?' },
    { id: '2', text: 'How can I make my writing more concise?' },
    { id: '3', text: 'Help me write a professional email' }
  ],
  'GFK': [
    { id: '1', text: 'What are the latest sales figures?' },
    { id: '2', text: 'Compare the January 11 Westlake figures to February 22' },
    { id: '3', text: 'Analyze the latest New England data' }
  ],
  // Add more workspace-specific questions here
};

// Helper function to get questions based on workspace name
const getWorkspaceQuestions = (workspaceName: string): SuggestedQuestion[] => {
  // Try to match workspace name (case-insensitive)
  const matchedQuestions = Object.entries(WORKSPACE_QUESTIONS).find(([key]) => 
    workspaceName.toLowerCase().includes(key.toLowerCase())
  );

  return matchedQuestions ? matchedQuestions[1] : [
    { id: '1', text: 'What information can you provide about this workspace?' },
    { id: '2', text: 'What are the key documents in this workspace?' },
    { id: '3', text: 'Can you summarize the main topics?' },
    { id: '4', text: 'What are the recent updates?' }
  ];
};

export function WorkspaceChatProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  const addWorkspace = async (workspace: Workspace) => {
    const updatedWorkspaces = [...workspaces, { ...workspace, createdAt: Date.now() }];
    
    try {
      // Save the workspace name mapping to the server
      const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL || 'http://localhost:4001';
      const userId = session?.user?.uuid || '';
      await fetch(`${serverUrl}/api/documents/workspace-names?userId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.NEXT_PUBLIC_API_KEY || ''}`
        },
        body: JSON.stringify({
          workspaceId: workspace.id,
          name: workspace.name
        })
      });
    } catch (error) {
      console.error('Error saving workspace name:', error);
    }

    setWorkspaces(updatedWorkspaces);
  };

  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!session?.user?.uuid) return;

      try {
        const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL || 'http://localhost:4001';
        const userId = session?.user?.uuid;
        
        // Fetch workspaces and their names
        const [workspacesResponse, namesResponse] = await Promise.all([
          fetch(`${serverUrl}/api/documents/namespaces?userId=${userId}`),
          fetch(`${serverUrl}/api/documents/workspace-names?userId=${userId}`)
        ]);

        const [workspacesData, namesData] = await Promise.all([
          workspacesResponse.json(),
          namesResponse.json()
        ]);

        console.log('Fetched workspace data:', workspacesData);
        console.log('Fetched names data:', namesData);

        // Add writing assistant to workspaces
        const writingAssistant = {
          id: 'writing-assistant',
          name: 'Writing Assistant',
          createdAt: Date.now(),
          locked: false,
          isSpecialAgent: true, // Flag to identify special agents
          suggestedQuestions: WORKSPACE_QUESTIONS['Writing-Assistant']
        };

        const transformedWorkspaces = [
          writingAssistant,
          ...workspacesData.workspaces.map((w: any) => {
            const name = namesData[w.id] || `${w.id} Agent`;
            return {
              id: w.id,
              name,
              createdAt: Date.now(),
              locked: false,
              suggestedQuestions: getWorkspaceQuestions(name)
            };
          })
        ];

        setWorkspaces(transformedWorkspaces);
      } catch (error) {
        console.error('Error fetching workspaces:', error);
      } finally {
        setLoading(false);
      }
    };

    if (session?.user?.uuid) {
      fetchWorkspaces();
    }
  }, [session]);

  return (
    <WorkspaceChatContext.Provider value={{ 
      workspaces, 
      setWorkspaces, 
      loading,
      addWorkspace,
      workspace,
      setWorkspace
    }}>
      {children}
    </WorkspaceChatContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceChatContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceChatProvider');
  }
  return {
    workspace: context.workspace,
    setWorkspace: context.setWorkspace,
    loading: context.loading
  };
}

export function useWorkspaceChatContext() {
  return useContext(WorkspaceChatContext);
}